import { Button } from "@/modules/shadcn/ui/button";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/modules/shadcn/ui/table";
import type { Organization } from "@repo/schema";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useAssignDeviceMutation,
  useGetUnassignedDevicesQuery,
  useRevokeDeviceMutation,
} from "../services/deviceApi";
import { formatDate } from "@/core/i18n";

export interface UnassignedDeviceListProps {
  organization: Organization;
}

/**
 * Devices that have enrolled but belong to nobody — the fulfilment queue.
 *
 * Enrollment is open, so anything can create a row here; assigning one is the
 * step that actually grants access, which is why it is restricted to super
 * admins and why nothing is reachable until it happens.
 *
 * Blocking is deliberately separate from revoking a device in the fleet table:
 * "the customer returned the box" and "this device is compromised" must not
 * collapse into one action. Revoking releases the device back into this queue;
 * blocking is terminal and drops it out of every list for good — hence the
 * blunter label on an action that shares the `revoke` endpoint underneath.
 */
export const UnassignedDeviceList = ({ organization }: UnassignedDeviceListProps) => {
  const { t } = useTranslation(["device", "common"]);
  const { data, isLoading } = useGetUnassignedDevicesQuery();
  const [assignDevice, { isLoading: isAssigning }] = useAssignDeviceMutation();
  const [revokeDevice, { isLoading: isRevoking }] = useRevokeDeviceMutation();

  const devices = data?.devices ?? [];

  const handleAssign = async (deviceId: number) => {
    try {
      await assignDevice({ deviceId, organizationId: organization.id }).unwrap();
      toast.success(t("unassigned.assigned", { name: organization.name }));
    } catch {
      toast.error(t("unassigned.assignFailed"));
    }
  };

  const handleRevoke = async (deviceId: number) => {
    try {
      await revokeDevice({ deviceId }).unwrap();
      toast.success(t("unassigned.blocked"));
    } catch {
      toast.error(t("unassigned.blockFailed"));
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h5 className="text-xl font-semibold">{t("unassigned.title")}</h5>
        <p className="text-sm text-black/45">
          {t("unassigned.description", { name: organization.name })}
        </p>
      </div>

      {devices.length === 0 ? (
        <p className="text-sm text-black/45">{t("unassigned.empty")}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("columns.device")}</TableHead>
              <TableHead>{t("columns.cameras")}</TableHead>
              <TableHead>{t("columns.firmware")}</TableHead>
              <TableHead>{t("columns.firstSeen")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id}>
                <TableCell className="font-mono text-xs">
                  {device.deviceId}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {device.cameras.map((c) => c.mxid).join(", ") || "-"}
                </TableCell>
                <TableCell>{device.firmwareVersion ?? "-"}</TableCell>
                <TableCell>
                  {formatDate(device.createdAt)}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      disabled={isAssigning}
                      onClick={() => handleAssign(device.id)}
                    >
                      {t("unassigned.assign")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isRevoking}
                      onClick={() => handleRevoke(device.id)}
                    >
                      {t("unassigned.blockPermanently")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
